import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useNotionSourceSettingsText,
  useNotionSourceSettingsViewModel,
  type NotionPage,
} from "@onetasklist/shared/sources/notion";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface NotionSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const NotionSourceSettings: React.FC<NotionSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useNotionSourceSettingsText(t);
  const vm = useNotionSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<NotionPage[]>([]);

  useEffect(() => {
    if (!vm.importTodos) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      void vm.searchPages(searchTerm).then(setSearchResults);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, vm]);

  const pageOptionById = useMemo(() => {
    const map = new Map<string, NotionPage>();
    vm.selectedPages.forEach((page) => map.set(page.id, page));
    searchResults.forEach((page) => map.set(page.id, page));
    return map;
  }, [searchResults, vm.selectedPages]);

  const selectedPageIds = vm.selectedPages.map((page) => page.id);

  if (!vm.isVisible) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.notion.sectionContent",
      )}
    >
      <Field
        orientation="horizontal"
        label={text.importTodosLabelText}
        hint={text.importTodosHintText}
      >
        <Checkbox
          checked={vm.importTodos}
          onChange={(_, data) => vm.onToggleImportTodos(Boolean(data.checked))}
        />
      </Field>

      {vm.importTodos && (
        <>
          <Field
            orientation="horizontal"
            label={text.pagesLabelText}
            hint={text.pagesHintText}
          >
            <SourceMultiTagPicker
              isLoading={vm.isSearchingPages}
              selectedIds={selectedPageIds}
              query={searchTerm}
              placeholder={text.pagesSearchPlaceholderText}
              options={Array.from(pageOptionById.values()).map((page) => ({
                id: page.id,
                label: page.title ?? page.id,
              }))}
              onQueryChange={setSearchTerm}
              onSelectionChange={(nextSelectedIds) => {
                const selectedPages = nextSelectedIds
                  .map((id) => pageOptionById.get(String(id)))
                  .filter((page): page is NotionPage => Boolean(page));
                vm.onSelectPages(selectedPages);
              }}
              minQueryLength={2}
              typeToSearchText={text.pagesHintText}
            />
          </Field>

          <Field orientation="horizontal" label={text.mentionsUserLabelText}>
            <Checkbox
              checked={vm.mentionsUser}
              onChange={(_, data) =>
                vm.onToggleMentionsUser(Boolean(data.checked))
              }
            />
          </Field>
        </>
      )}

      {vm.isSearchingPages && <Text>{text.loadingPagesText}</Text>}
      {vm.hasError && <Text>{text.loadErrorText}</Text>}
    </SettingsSectionCard>
  );
};
